import React, { useState, useEffect, useRef, useCallback } from 'react';
import {useDispatch} from "react-redux";
import {useHistory} from "react-router-dom";
import {useSnackbar} from "notistack";
import clsx from 'clsx';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import { makeStyles } from '@mui/styles';
import Icon from "@mui/material/Icon";
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardMedia from '@mui/material/CardMedia';
import CardActions from '@mui/material/CardActions';
import Tooltip from "@mui/material/Tooltip";
import CallIcon from '@mui/icons-material/Call';
import EmailIcon from '@mui/icons-material/Email';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CachedIcon from '@mui/icons-material/Cached';
import CodeIcon from '@mui/icons-material/Code';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Alert from "@mui/material/Alert";
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import InputBase from '@mui/material/InputBase';
import CloseIcon from '@mui/icons-material/Close';
import _ from 'lodash';

import {isMobile} from 'is-mobile';

import {getForms, removeForm, resetCount} from "../store/formSlice";

const useStyles = makeStyles(theme => ({
    root: {},
    board: {
        cursor: 'pointer',
        transitionProperty: 'box-shadow border-color',
        transitionDuration: '0.5s',
        transitionTimingFunction: '0.25s'
    },
    newBoard: {},
    statIcon: {
        fontSize: '1rem !important',
        padding: '5px 0 !important',

        '&:hover': {
            backgroundColor: 'transparent !important'
        },

        '& svg': {
            fontSize: '1rem !important',
            marginRight: 5
        }

    },
    code: {
        cursor: 'pointer',
        position: 'relative',
        backgroundColor: '#eaeaea',
        borderRadius: 5,
        fontFamily: 'ui-monospace,"Cascadia Mono","Segoe UI Mono","Liberation Mono",Menlo,Monaco,Consolas,monospace',
        fontSize: 13,
        lineHeight: 1,
        margin: 0,
        padding: '15px',
        transition: 'background-color .5s ease',

        '&:after': {
            content: '"Copy to Clipboard"',
            display: 'block',
            position: 'absolute',
            bottom: 6,
            right: 6,
            fontSize: 11,
        },

        '&.highlighted': {
            background: '#f7ffcc',

            '&:after': {
                content: '"Copied"',
            }


        }

    }
}));

const API_BASE = window.location.hostname;

export default function Home(props) {

    const dispatch = useDispatch();

    const searchRef = useRef(null);

    const urlParams = new URLSearchParams(document.location.search);

    const [search, setSearch] = useState(urlParams.get("search") ? urlParams.get("search") : '');
    const [page, setPage] = useState(parseInt(urlParams.get("page")) > 1 ? parseInt(urlParams.get("page")) : 1);

    const mobile = isMobile();

    const classes = useStyles(props);

    const history = useHistory();

    const [loading, setLoading] = useState(true);

    const { enqueueSnackbar } = useSnackbar();

    const [forms, setForms] = useState([]);

    const [previewOpen, setPreviewOpen] = useState(false);

    const [previewUrl, setPreviewUrl] = useState(null);

    const [codeForm, setCodeForm] = useState(null);

    const codeBlockRefs = useRef([]);

    // for code dialog
    const [openCode, setOpenCode] = useState(false);

    const [accordionExpanded, setAccordionExpanded] = useState('panel1');

    const handleAccordionChange = (panel) => (event, isExpanded) => {
        setAccordionExpanded(isExpanded ? panel : false);
    };

    const handleOpenCode = (id) => {

        const url = API_BASE === 'localhost' ? `render.html?formID=${id}` : `https://sendiio.com/form/${id}/render`;

        setCodeForm(id);

        setPreviewUrl(url);

        setOpenCode(true);

    };

    const handleCloseCode = () => {

        setOpenCode(false);

    };

    const handlePreviewOpen = (id) => {

        const url = API_BASE === 'localhost' ? `render.html?formID=${id}` : `https://sendiio.com/form/${id}/render`;

        setPreviewUrl(url);

        setPreviewOpen(true);

    };

    const handlePreviewClose = () => {
        setPreviewOpen(false);
    };

    const handleCustomForm = () => {

        history.push(`/form`);

    }

    const handleRemoveForm = (id) => {

        if (window.confirm("Do you really want to remove selected form?")) {

            dispatch(removeForm(id))
                .then((response) => {

                    if (response.payload) {

                        if(response.payload.status === 200) {

                            enqueueSnackbar('Selected form was successfully removed!', {
                                variant: 'success'
                            });

                            dispatch(getForms({
                                'startFrom': page,
                                'search': ''
                            })).then((getFormResponse) => {

                                if(getFormResponse.payload){

                                    if(getFormResponse.payload.error === 0){

                                        setForms(getFormResponse.payload.data);

                                    }

                                }

                                setLoading(false);

                            });

                        } else {

                            enqueueSnackbar(response.payload.msg, {
                                variant: 'error'
                            });

                            history.push('/');

                        }

                    } else {

                        enqueueSnackbar('An error occurred when you tried to delete the form!', {
                            variant: 'error'
                        });

                    }

                });

        }

    }

    const handleOpenForm = (id) => {

        history.push(`/form/${id}`);

    }

    const handleResetStats = (item) => {

        const formData = new FormData();

        const formId = parseInt(item.id);

        formData.append('form_id', formId);

        dispatch(resetCount(formData)).then((response) => {

            if(response.payload){

                if(response.payload.error === 0){

                    dispatch(getForms({
                        'startFrom': page,
                        'search': ''
                    })).then((formsResponse) => {

                        if(formsResponse.payload && formsResponse.payload.error === 0){

                            setForms(formsResponse.payload.data);

                        }

                        enqueueSnackbar('Statistics has been successfully updated!', {
                            variant: 'success'
                        });

                    });

                } else {

                    enqueueSnackbar(response.payload.msg, {
                        variant: 'error'
                    });

                }

            } else {

                enqueueSnackbar('An error occurred when we tried to update statistics!', {
                    variant: 'error'
                });

            }

        });

    }

    const copyCode = (codeType) => {

        codeBlockRefs.current[codeType].classList.add('highlighted');

        let code = null;
        let script = null;

        switch (codeType){

            case 'shortHtml':

                code = codeBlockRefs.current[codeType].querySelector('code').innerHTML;

                break;

            case 'fullHtml':

                const iframe = document.querySelector('iframe#previewIframe');

                code = iframe.contentWindow.document.querySelector('#sendiio-subscription').outerHTML;

                break;

            case 'javascript1':

                code = codeBlockRefs.current[codeType].querySelector('code').innerHTML;

                break;

            case 'javascript2':

                code = codeBlockRefs.current[codeType].querySelector('code').innerHTML;

                break;

            case 'javascript3':

                code = codeBlockRefs.current[codeType].querySelector('code').innerHTML;

                break;

            case 'iframe':

                code = codeBlockRefs.current[codeType].querySelector('code').innerHTML;

                break;

            default:

                return false;

        }

        code = code.replaceAll('&lt;', '<');
        code = code.replaceAll('&gt;', '>');

        if(codeType === 'iframe'){

            let iframeParams = ' ';

            const iframe = document.querySelector('#previewIframe').contentWindow.document.body;

            if(iframe){

                const iframeWidth = iframe.offsetWidth;
                const iframeHeight = iframe.offsetHeight;

                iframeParams = ` width="100%" height="640px" `;

            }

            code = code.replace(' ', iframeParams);

        }

        copyToClipboard(code);

        setTimeout(() => {

            codeBlockRefs.current[codeType].classList.remove('highlighted');

        }, 1000);

    }

    const copyToClipboard = (textToCopy) => {
        navigator.clipboard.writeText(textToCopy)
            .catch(err => {

                console.log('Error with copyToClipboard:', err);

                return false;

            });
    }

    const handleDebounceSearchFn = (value) => {

        dispatch(getForms({
            'startFrom': page,
            'search': value
        })).then((getFormsResponse) => {

            if(getFormsResponse.payload){

                if(getFormsResponse.payload.error === 0){

                    setForms(getFormsResponse.payload.data);
                    urlParams.set('page', 1);
                    urlParams.set('search', value);
                    setPage(1);
                    setSearch(value);

                    window.history.replaceState( {} , '/', `?${urlParams.toString()}` );

                } else {

                    enqueueSnackbar(getFormsResponse.payload.message, {
                        variant: 'error'
                    });

                }

            } else {

                enqueueSnackbar('An error occurred when we tried to get your forms!', {
                    variant: 'error'
                });

            }

        });

    }

    const debounceSearchFn = useCallback(_.debounce(handleDebounceSearchFn, 1000), []);

    const handleSearchChange = (e) => {
        setSearch(e.target.value);
        debounceSearchFn(e.target.value);
    };

    useEffect(() => {

        dispatch(getForms({
            'startFrom': page,
            'search': search
        })).then((response) => {

            if(response.payload){

                if(response.payload.error === 0){

                    setForms(response.payload.data);

                } else {

                    enqueueSnackbar(response.payload.msg, {
                        variant: 'error'
                    });

                }

            } else {

                enqueueSnackbar('An error occurred when we tried to get your forms!', {
                    variant: 'error'
                });

            }

            setLoading(false);

        });

    }, []);

    if (loading) {
        return <Box sx={{ width: '100%' }}>
            <LinearProgress />
        </Box>;
    }

    return <>

        <Typography className="mt-48" typography={mobile ? 'h4' : 'h2'} textAlign="center">Create a Form</Typography>

        <Grid container spacing={3} sx={{ mt: 0, mb: 4 }} className="flex flex-wrap w-full justify-center p-16" style={{maxWidth: 1200, margin: !mobile && '0 auto'}}>

            <Grid item xs={12} lg={3}>
                <Paper
                    className={clsx(
                        classes.board,
                        classes.newBoard,
                        'flex flex-col items-center justify-center w-full h-full rounded-16 py-24'
                    )}
                    elevation={3}
                    onClick={e => handleCustomForm(e, 'script')}
                    role="button"
                    tabIndex={0}>
                    <Icon className="text-56" color="action">
                        add
                    </Icon>
                    <Typography className="text-20 text-center pt-16 px-32" color="inherit">
                        <b>Start from <br/> Scratch</b>
                    </Typography>
                    <p><small>A blank slate is all you need</small></p>
                </Paper>
            </Grid>

            <Grid item xs={12} lg={3}>
                <Paper
                    className={clsx(
                        classes.board,
                        classes.newBoard,
                        'flex flex-col items-center justify-center w-full h-full rounded-16 py-24'
                    )}
                    elevation={3}
                    onClick={() => { history.push(`/themes`) }}
                    role="button"
                    tabIndex={0}>
                    <Icon className="text-56" color="action">
                        palette
                    </Icon>
                    <Typography className="text-20 text-center pt-16 px-32" color="inherit">
                        <b>Use  <br/>Template</b>
                    </Typography>
                    <p><small>Choose from premade forms</small></p>
                </Paper>
            </Grid>

        </Grid>

        {(forms && forms.data) && <>

            <Typography className="mt-64" typography={mobile ? 'h4' : 'h2'} textAlign="center">My Forms</Typography>

            {!(forms.data.length === 0 && search === '' && page === 1) &&

                <Paper
                    component="form"
                    sx={{ p: '2px 4px', m: '24px auto 12px auto', display: 'flex', alignItems: 'center', width: '100%', maxWidth: 480 }}
                >
                    <InputBase
                        inputRef={searchRef}
                        sx={{ ml: 1, flex: 1, minHeight: 48 }}
                        placeholder="Search Forms"
                        defaultValue={search}
                        onChange={handleSearchChange}
                        inputProps={{ 'aria-label': 'search forms' }}
                    />
                    <IconButton
                        className={search === '' ? 'hidden' : ''}
                        type="button"
                        sx={{ p: '10px' }}
                        onClick={() => {
                            dispatch(getForms({
                                'startFrom': 1,
                                'search': ''
                            })).then((getFormsResponse) => {

                                if(getFormsResponse.payload){

                                    if(getFormsResponse.payload.error === 0){

                                        setForms(getFormsResponse.payload.data);
                                        urlParams.set('page', 1);
                                        urlParams.set('search', '');
                                        setPage(1);
                                        setSearch('');
                                        searchRef.current.value = '';

                                        if(API_BASE === 'localhost'){
                                            window.history.replaceState( {} , '/', '/');
                                        } else {
                                            window.history.replaceState( {} , '/', '/form-editor');
                                        }

                                    } else {

                                        enqueueSnackbar(getFormsResponse.payload.message, {
                                            variant: 'error'
                                        });

                                    }

                                } else {

                                    enqueueSnackbar('An error occurred when we tried to get your forms!', {
                                        variant: 'error'
                                    });

                                }

                            });
                        }}
                        aria-label="clear">
                        <CloseIcon />
                    </IconButton>
                </Paper>

            }

            <Grid container spacing={3} sx={{ mt: 0, mb: 4 }} className="flex flex-wrap w-full justify-center p-16 mb-90" style={{maxWidth: 1200, margin: !mobile && '0 auto'}}>

                {forms.data.length > 0 ? forms.data.map(item =>

                    <Grid item xs={12} lg={3} key={`key-form-wrapper-${item.id}`}>

                        <Card sx={{width: '100%', height: '100%'}} className="mb-24 pb-0 rounded-16">
                            <CardHeader
                                title={item.name}
                                className="pb-5"
                            />
                            <Typography sx={{fontSize: 14}} style={{display: 'flex', justifyContent: 'space-between', verticalAlign: 'middle'}} className="px-16" color="text.secondary" gutterBottom>
                                <Tooltip title="Views">
                                    <IconButton className={classes.statIcon}>
                                        <VisibilityIcon /> {item.views}
                                    </IconButton>
                                </Tooltip>
                                <span>
                                    <Tooltip title="Emails lists subscribes">
                                        <IconButton className={classes.statIcon} style={{marginRight: 10}}>
                                            <EmailIcon /> {item.emails_lists_subscribes}
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Phone numbers lists subscribes">
                                        <IconButton className={classes.statIcon}>
                                            <CallIcon /> {item.phone_numbers_lists_subscribes}
                                        </IconButton>
                                    </Tooltip>
                                </span>
                            </Typography>
                            <CardMedia
                                component="img"
                                height={360}
                                style={{objectFit: 'contain', backgroundSize: 'contain'}}
                                image={API_BASE === 'localhost' ? item.preview : `https://sendiio.com/forms/${item.id}/preview`}
                                alt="Form"
                            />
                            <CardActions disableSpacing>
                                <Tooltip title="Preview Form">
                                    <IconButton aria-label="View" onClick={() => {handlePreviewOpen(item.id)}}>
                                        <VisibilityIcon/>
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Edit Form">
                                    <IconButton aria-label="Edit" onClick={() => {handleOpenForm(item.id)}}>
                                        <EditIcon/>
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Reset Stats">
                                    <IconButton aria-label="reset" onClick={() => handleResetStats(item)}>
                                        <CachedIcon/>
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Get a Code">
                                    <IconButton aria-label="code" onClick={() => handleOpenCode(item.id)}>
                                        <CodeIcon/>
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Remove Form">
                                    <IconButton aria-label="Remove" sx={{marginLeft: 'auto'}} onClick={() => {handleRemoveForm(item.id)}}>
                                        <DeleteIcon/>
                                    </IconButton>
                                </Tooltip>
                            </CardActions>
                        </Card>

                    </Grid>) :

                    <>

                        {(search === '' && page === 1) ?

                            <Alert severity="info">You didn’t create any forms yet. If you’re looking for your older forms please check the <a href="https://sendiio.com/forms/manage" target="_blank">legacy forms section</a>.</Alert>

                            :

                            <Alert severity="info">Nothing was found! Please change your query or clear the search field.</Alert>


                        }

                    </>

                }

            </Grid>

            {!(forms.data.length === 0 && search === '' && page === 1) &&

                <Stack
                    justifyContent="center"
                    alignItems="center"
                    alignContent="center"
                    spacing={2}
                    className="mb-50"
                >
                    <Pagination
                        count={Math.ceil(forms.total/forms.per_page)}
                        page={page}
                        color="primary"
                        onChange={(e, value) => {

                            dispatch(getForms({
                                'search': search,
                                'startFrom': value
                            })).then((getFormsResponse) => {

                                if(getFormsResponse.payload){

                                    if(getFormsResponse.payload.error === 0){

                                        setForms(getFormsResponse.payload.data);

                                        setPage(value);

                                        urlParams.set('page', value);

                                        window.history.replaceState( {} , '/', `?${urlParams.toString()}` );

                                    } else {

                                        enqueueSnackbar(getFormsResponse.payload.message, {
                                            variant: 'error'
                                        });

                                    }

                                } else {

                                    enqueueSnackbar('An error occurred when we tried to get your forms!', {
                                        variant: 'error'
                                    });

                                }

                            });

                        }} />
                </Stack>

            }

            <br/>

            <Dialog open={previewOpen} onClose={handlePreviewClose} maxWidth="xl" fullWidth={true}>
                <DialogTitle align="center">Form Preview</DialogTitle>
                <DialogContent dividers>
                    <iframe title="Your form preview" src={previewUrl} width="100%" height="640px" frameBorder="none"></iframe>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handlePreviewClose}>Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openCode} onClose={handleCloseCode} maxWidth="lg" fullWidth={true}>
                <DialogTitle align="center">Here is your subscribe form code</DialogTitle>
                <DialogContent dividers>
                    <div>
                        <Accordion defaultExpanded={true} expanded={accordionExpanded === 'panel1'} onChange={handleAccordionChange('panel1')}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls="panel1a-content"
                                id="panel1a-header"
                            >
                                <Typography fontWeight="bold">Use a Script & Full Code</Typography>
                            </AccordionSummary>
                            <AccordionDetails style={{paddingBottom: 60}}>

                                <Alert severity="info">
                                    This method uses HTML code. This way you will have full control over your form,
                                    but you need to update it every time you update the form in the Sendiio app.
                                </Alert>

                                <p>Copy and put &lt;div&gt; in place where you want the subscribe form will shown:</p>

                                <div ref={el => codeBlockRefs.current['fullHtml'] = el} className={classes.code} onClick={() => copyCode('fullHtml')}>
                                    <code>
                                        &lt;div id='sendiio-subscription' class='sendiio-form-default' form-generated='true' form-id='{codeForm}'&gt;...&lt;/div&gt;
                                    </code>
                                </div>

                                <p>You can put a &lt;script&gt; code where ever you want:</p>

                                <div ref={el => codeBlockRefs.current['javascript1'] = el} className={classes.code} onClick={() => copyCode('javascript1')}>
                                    <code>&lt;script src="https://sendiio.com/form_editor/builder.js"&gt;&lt;/script&gt;</code>
                                </div>

                            </AccordionDetails>
                        </Accordion>
                        <Accordion expanded={accordionExpanded === 'panel2'} onChange={handleAccordionChange('panel2')}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls="panel2a-content"
                                id="panel2a-header"
                            >
                                <Typography fontWeight="bold">Use a Script & Short Code</Typography>
                            </AccordionSummary>
                            <AccordionDetails style={{paddingBottom: 60}}>

                                <Alert severity="info">
                                    This method uses javascript to render your HTML form.
                                    Thus you will always have up-to-date form, without the need to update it.</Alert>

                                <p>Copy and put &lt;div&gt; in place where you want the subscribe form will shown:</p>

                                <div ref={el => codeBlockRefs.current['shortHtml'] = el} className={classes.code} onClick={() => copyCode('shortHtml')}>
                                    <code>
                                        &lt;div id='sendiio-subscription' class='sendiio-form-default' form-id='{codeForm}'&gt;&lt;/div&gt;
                                    </code>
                                </div>

                                <p>You can put a &lt;script&gt; code where ever you want:</p>

                                <div ref={el => codeBlockRefs.current['javascript2'] = el} className={classes.code} onClick={() => copyCode('javascript2')}>
                                    <code>&lt;script src="https://sendiio.com/form_editor/builder.js"&gt;&lt;/script&gt;</code>
                                </div>

                            </AccordionDetails>
                        </Accordion>
                        <Accordion expanded={accordionExpanded === 'panel3'} onChange={handleAccordionChange('panel3')}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls="panel3a-content"
                                id="panel3a-header"
                            >
                                <Typography fontWeight="bold">Use an iFrame</Typography>
                            </AccordionSummary>
                            <AccordionDetails style={{paddingBottom: 60}}>

                                <Alert severity="info">
                                    This method uses iframe code. Thus you will get:
                                    always up-to-date form,
                                    easy to grasp and use technology,
                                    will display your form without any issues and conflicts with your website styles,
                                    but with poor controllability and adaptability.</Alert>

                                <p>Copy and put &lt;iframe&gt; in place where you want the subscribe form will shown:</p>

                                <div ref={el => codeBlockRefs.current['iframe'] = el} className={classes.code} onClick={() => copyCode('iframe')}>
                                    <code>&lt;iframe id="iframeSendiio" src="https://sendiio.com/form/{codeForm}/render" frameBorder="none"&gt;&lt;/iframe&gt;</code>
                                </div>

                                <p>You can put a &lt;script&gt; code where ever you want. It is necessary to properly redirect users after submitting the form:</p>

                                <div ref={el => codeBlockRefs.current['javascript3'] = el} className={classes.code} onClick={() => copyCode('javascript3')}>
                                    <code>&lt;script src="https://sendiio.com/form_editor/redirect.js"&gt;&lt;/script&gt;</code>
                                </div>

                                <br/>

                                <iframe id="previewIframe"
                                        title="Your form preview"
                                        src={previewUrl}
                                        width="100%"
                                        height="640px"
                                        frameBorder="none"></iframe>

                            </AccordionDetails>
                        </Accordion>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseCode}>Close</Button>
                </DialogActions>
            </Dialog>

        </>}

    </>;
}